import { ChangeEvent, useContext, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  styledComponent,
  Grid,
  InputAdornment,
  Stack,
  Chip,
  SvgIcon,
  SearchIcon,
  useTheme
} from '@hermes/web-components'
import { ModalLayoutProps } from '../../../types'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { AppDataContext } from '../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../providers/SearchProfilesPage'
import { useTimeout } from '../../../hooks/useTimeout'
import { ProfileKeyword, ProfileKeywordRelated } from '../../../types/keywords'
import { MobileDetailsModal } from '../MobileDetailsModal'

interface MobileConditionsProceduresModalProps extends ModalLayoutProps {
  specialtyName: string
}

const ModalContentWrapper = styledComponent(Box)(() => ({
  height: '60vh',
  width: '100%',
  overflowY: 'scroll'
}))

const getKeywordsBySections = ({ keywords, language }: { keywords: ProfileKeywordRelated[]; language: string }) => {
  if (keywords.length === 0) {
    return []
  }
  const map = ((m, a) => (
    a.forEach((s) => {
      const res = m.get(s.name[language][0]) || []
      m.set(s.name[language][0], (res.push(s), res))
      // eslint-disable-next-line no-sequences
    }),
    m
  ))(new Map(), keywords)

  return Array.from(map, ([name, value]) => ({ name, value })).sort((a, b) => a.name.localeCompare(b.name))
}

const MobileConditionsProceduresModal = ({
  specialtyName,

  ...props
}: MobileConditionsProceduresModalProps) => {
  const translate = useTranslateMessage()
  const { language } = useContext(AppDataContext)
  const {
    changeSpecialty,
    state: { filters, relatedKeywords }
  } = useContext(SearchProfilesPageContext)

  const [searchString, setSearchString] = useState<string>('')

  const sortedKeywords = useMemo(() => {
    let modifiedKeywords = relatedKeywords
    if (searchString) {
      modifiedKeywords = modifiedKeywords.filter((item) =>
        item.name[language].toLowerCase().includes(searchString.toLowerCase())
      )
    }
    return getKeywordsBySections({ keywords: modifiedKeywords, language })
  }, [relatedKeywords, language, searchString])

  const timeout = useTimeout()

  const onChangeSearch = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    timeout(() => setSearchString(ev.target.value))

  const customTheme = useTheme()
  const heading = translate('search.all_cond_proc_for', { specialtyName })

  const onClickKeyword = (id: string, slug: string, name: string) => () => {
    changeSpecialty(id, slug, true, name)
    props.onClose()
  }

  return (
    <MobileDetailsModal
      heading={heading}
      {...props}
      headingProps={{
        sx: { color: 'text.secondary', '& b': { color: `${customTheme.palette.text.primary}!important` } }
      }}
    >
      <Grid container item position="relative">
        <TextField
          onChange={onChangeSearch}
          size="small"
          placeholder={translate('search')}
          fullWidth
          InputProps={{
            sx: { height: 40 },
            endAdornment: (
              <InputAdornment position="end">
                <SvgIcon sx={{ color: 'text.black700' }} fontSize="small">
                  <SearchIcon />
                </SvgIcon>
              </InputAdornment>
            )
          }}
        />
        <ModalContentWrapper mt={1}>
          {sortedKeywords.map((arr: { name: string; value: ProfileKeyword[] }, index: number) => (
            <Stack key={arr.name + index} alignItems="flex-start" gap={1}>
              <Typography mt={1}>{arr.name}</Typography>
              {arr.value.map((keyword) => {
                const isSelected = filters.specialties.includes(keyword.id.toString())

                return (
                  <Chip
                    key={keyword.id}
                    onClick={onClickKeyword(keyword.id.toString(), keyword.slug[0], keyword.name[language])}
                    label={keyword.name[language]}
                    sx={{ mr: 0.5 }}
                    size="small"
                    variant={isSelected ? 'selected' : 'option'}
                  />
                )
              })}
            </Stack>
          ))}
        </ModalContentWrapper>
      </Grid>
    </MobileDetailsModal>
  )
}

export default MobileConditionsProceduresModal
